import React, {useEffect, useRef, useState} from 'react';

import {
  Breakpoints,
  ButtonAppearance,
  ButtonCustom,
  CloseButton,
  Filter,
  TextCustom,
  useIsMobile,
} from '@vw-marketing/us-components';

import {
  filterNameMap,
  onVWBasicFilterSelectClick,
} from '../../tagging/tagging-helpers';
import {
  FilterTracking,
  useTrackingManager,
} from '../../hooks/use-tracking-manager';
import {
  StyledDropdownContainer,
  StyledFiltersBarContainer,
  StyledFiltersContentContainer,
  StyledFiltersSectionContainer,
  StyledOtherFiltersContainer,
  StyledStickyFiltersContainer,
} from './styles';
import {AppFilters} from '../../typing/local-storage';
import {blockScrollAction, formatRangeLimit} from '../../utils/general';
import {DEFAULT_FILTERS_CONFIG} from '../../utils/app-constants';
import {FilterItem} from '../../typing/used-inventory';
import {filterMobileTextStyle, getResultsLabel} from './helpers';
import {FilterOnlineBuying} from '../filter-online-buying/FilterOnlineBuying';
import {SearchResultsSortV2} from '../search-results-sort-v2';
import {useFeatureAppConfig} from '../../hooks/use-feature-app-config';
import {useTranslations} from '../../hooks/use-translations';
import FilterAppliedBar from '../filter-applied-bar';
import FilterButton from '../filter-button';
import FilterDealer from '../filter-dealer';
import FilterDistance from '../filter-distance';
import FilterExteriorColor from '../filter-exterior-color';
import FilterFuelType from '../filter-fuel-type';
import FilterInteriorColor from '../filter-interior-color';
import FilterMileage from '../filter-mileage';
import FilterModel from '../filter-model';
import FilterPrice from '../filter-price';
import FilterTransmission from '../filter-transmission';
import FilterYear from '../filter-year';
import FilterZip from '../filter-zip';
import LineDivisor from '../line-divisor';
import StickyScrollContainer from '../sticky-scroll-container';
import {globalIsSticky} from '../../store/local-storage';
import {useReactiveVar} from '@apollo/client';

interface SearchFiltersSectionProps {
  filters?: AppFilters;
  sortByValue: string;
  totalPages: number;
  totalVehicles?: number;
  onDeleteAllElements: () => void;
  onDeleteElement: (item: FilterItem) => void;
  onHandleClearAllFilters: () => void;
  onHandleFilterChange: (item: FilterItem) => void;
  onHandleSelectAllFilter: (items: FilterItem[], isChecked: boolean) => void;
  onHandleSliderChange: (selectedNum: number | number[], type: string) => void;
  onHandleSortChange: (option?: string) => void;
}

type modalStatus = 'opened' | 'hidden';

const DEFAULT_DROPDOWN_OPTIONS = [
  {value: 'distance', label: 'Distance'},
  {value: 'adjustedListPrice', label: 'Price'},
  {value: 'odometer', label: 'Mileage'},
  {value: 'model', label: 'Model'},
];

/**
 * Aux get current option object
 * @param options
 * @param selectedValue
 * @returns
 */
const getSelectedOption = (options: any[], selectedValue) =>
  options.find(option => option.value === selectedValue);

const SearchFiltersSection = ({
  filters,
  sortByValue,
  totalPages,
  totalVehicles,
  onHandleFilterChange,
  onHandleSliderChange,
  onHandleSelectAllFilter,
  onHandleClearAllFilters,
  onHandleSortChange,
}: SearchFiltersSectionProps) => {
  const isMobile: boolean = useIsMobile(Breakpoints.b960);
  const isSticky: boolean = useReactiveVar(globalIsSticky);
  const [_, setShowOption] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [labelValue, setLabelValue] = useState<
    {label: string; value: string} | undefined
  >(undefined);
  const {sortFilterOptions = DEFAULT_DROPDOWN_OPTIONS} = useFeatureAppConfig();
  const trackingManager = useTrackingManager();
  const filterTranslations = useTranslations().resultsSection.filters;
  const {
    dealers: dealerTranslation,
    buttonShowNumResultsLabel,
    distance: distanceTranslation,
    price: priceTranslation,
    models: modelTranslation,
    years: yearTranslation,
    fuelType: fuelTypeTranslation,
    transmission: transmissionTranslation,
    mileage: mileageTranslation,
    exteriorColor: exteriorColorTranslation,
    interiorColor: interiorColorTranslation,
  } = filterTranslations;
  const {price, distance, mileage} = {
    ...DEFAULT_FILTERS_CONFIG,
    ...useFeatureAppConfig().filterConfig,
  };

  const [modalState, setModalState] = useState<modalStatus>('hidden');
  const [userPrice, setUserPrice] = useState<number[]>(
    formatRangeLimit(filters?.price || price.default || [], price.maxDistance),
  );
  const [userMileage, setUserMileage] = useState<number[]>(
    filters?.odometer || mileage.default || [],
  );
  const modalFilterRef = useRef<HTMLDivElement | null>(null);

  const showNumResults = getResultsLabel(
    buttonShowNumResultsLabel,
    totalVehicles || 0,
  );

  const filterSelectTracking = (filterType: string, filterName: string) => {
    let values;
    let filter = {name: filterName, type: filterType} as FilterTracking;
    const link = {name: 'Results'};

    switch (filterName) {
      case filterNameMap.dealers:
      case filterNameMap.model:
      case filterNameMap.year:
        //Override filterName
        filterName =
          filterName === filterNameMap.dealers
            ? 'dealers'
            : filterName === filterNameMap.model
            ? 'modelName'
            : filterName === filterNameMap.year
            ? 'modelYear'
            : filterName;
        const appliedFiltersByType = filters?.appliedFilters?.filter(
          item => item.type === filterName,
        );
        values = appliedFiltersByType?.map(item =>
          item.type !== 'dealers' ? item?.value : item?.key,
        );
        values && (filter.values = values);
        break;
      case filterNameMap.price:
        if (filters?.price?.length) {
          filter.lowerbound = filters.price[0]?.toString();
          filter.upperbound =
            filters?.price[1] !== -1
              ? filters?.price[1]?.toString()
              : `${DEFAULT_FILTERS_CONFIG.price.maxDistance}+`;
        }
        break;
      default:
        break;
    }

    onVWBasicFilterSelectClick(filter, link, trackingManager);
  };

  const showFiltersTaggingAction = (filterType: string, filterName: string) => {
    if (modalState === 'opened') {
      filterSelectTracking(filterType, filterName);
    }
  };

  /**
   * Handle dropdown change event
   * @param value
   */
  const handleDropdownChange = (value: string) => {
    const option = getSelectedOption(sortFilterOptions, value);
    setShowOption(false);
    if (option) {
      setLabelValue(option);
      value && onHandleSortChange(option.value);
    }
  };

  const onHandleFiltersModalChange = () => {
    setModalState(modalState === 'hidden' ? 'opened' : 'hidden');
    showFiltersTaggingAction('Filters', 'complex');
  };

  const onHandleFilterPriceApply = () => {
    let rangeValue = userPrice;
    if (rangeValue[1] === price.maxDistance) {
      rangeValue = [rangeValue[0] || 0, -1];
    }
    onHandleSliderChange(rangeValue, 'price');
  };

  const onHandleFilterMileageApply = () => {
    onHandleSliderChange(userMileage, 'mileage');
  };

  const onHandleComponentResize = () => {
    if (modalState === 'opened') {
      setModalState('hidden');
    }
  };

  useEffect(() => {
    if (modalState === 'opened') {
      if (modalFilterRef && modalFilterRef.current) {
        //@ts-ignore
        modalFilterRef.current.scrollTo({top: 0, behavior: 'instant'});
      }
      blockScrollAction(true);
    }
    window.addEventListener('resize', onHandleComponentResize);
    return () => {
      blockScrollAction(false);
      window.addEventListener('resize', onHandleComponentResize);
    };
  }, [modalState]);

  useEffect(() => {
    if (filters?.price !== userPrice && filters?.price?.length) {
      setUserPrice(formatRangeLimit(filters.price, price.maxDistance));
    }
  }, [filters?.price]);

  useEffect(() => {
    if (filters?.odometer !== userMileage && filters?.odometer?.length) {
      setUserMileage(formatRangeLimit(filters?.odometer, mileage.maxDistance));
    }
  }, [filters?.odometer]);

  let userDistance = filters?.distance || 0;
  userDistance = userDistance === -1 ? distance.maxDistance : userDistance;

  /**
   * Hide dropdown option box when user is interacting with another component
   * @param event
   */
  const handleMouseDown = (event: any) => {
    !dropdownRef?.current?.contains(event.target) && setShowOption(false);
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      document.addEventListener('mousedown', handleMouseDown, false);
    }
    return () => {
      if (typeof window !== 'undefined') {
        document.removeEventListener('mousedown', handleMouseDown, false);
      }
    };
  }, []);

  useEffect(() => {
    if (labelValue?.value !== sortByValue) {
      handleDropdownChange(sortByValue);
    }
  }, [sortByValue]);

  return (
    <StyledFiltersSectionContainer className="search-filter_main-container">
      <StickyScrollContainer>
        <StyledFiltersContentContainer
          className={`search-filter_filters ${modalState}`}
          id="search-filter_filters"
        >
          <div className="zipContainer" style={{minWidth: 258, flex: 1}}>
            <FilterZip />
          </div>

          {!isMobile && (
            <div className="distanceContainer" style={{minWidth: 450, flex: 1}}>
              <FilterDistance
                userDistance={userDistance}
                onHandleDistanceChange={onHandleSliderChange}
                translation={distanceTranslation}
                config={distance}
                isMobile={isMobile}
              />
            </div>
          )}
          <div className="filter-mobile-button">
            {/* @ts-ignore */}
            <ButtonCustom
              appearance={ButtonAppearance.Secondary}
              onClick={onHandleFiltersModalChange}
            >
              <Filter /> Filters
            </ButtonCustom>
          </div>
          <div className="modal-filters" id="modal-filter">
            <div className="modal_header-content">
              <TextCustom {...filterMobileTextStyle}>Filters</TextCustom>
              <CloseButton onClick={onHandleFiltersModalChange} />
            </div>
            <div className="modal_filters-content" ref={modalFilterRef}>
              {isMobile && (
                <div className="build_the_deal">
                  <FilterOnlineBuying />
                </div>
              )}
              <LineDivisor onlyMobile />
              {isMobile && (
                <FilterDistance
                  userDistance={userDistance}
                  onHandleDistanceChange={onHandleSliderChange}
                  translation={distanceTranslation}
                  config={distance}
                  isMobile={isMobile}
                />
              )}
              <LineDivisor onlyMobile />
              <div className="button-filters-wrapper">
                <div className="dealer-content">
                  <FilterButton
                    label={dealerTranslation.ctaLabel}
                    buttonLabel={showNumResults}
                    isDisabled={!!!filters?.dealers?.length}
                    filterSelectTracking={() =>
                      filterSelectTracking('multi_value', filterNameMap.dealers)
                    }
                  >
                    <FilterDealer
                      dealers={filters?.dealers || []}
                      onHandleDealerCheck={onHandleFilterChange}
                      translation={dealerTranslation}
                      isMobile={isMobile}
                    />
                  </FilterButton>
                </div>
                <LineDivisor onlyMobile />
                <div className="model-content">
                  <FilterButton
                    label="Model"
                    buttonLabel={showNumResults}
                    isDisabled={!!!totalVehicles}
                    filterSelectTracking={() =>
                      filterSelectTracking('multi_value', filterNameMap.model)
                    }
                  >
                    <FilterModel
                      translation={modelTranslation}
                      models={filters?.models}
                      onHandleModelSelection={onHandleFilterChange}
                      onHandleAllSelection={onHandleSelectAllFilter}
                      isMobile={isMobile}
                    />
                  </FilterButton>
                </div>
                <LineDivisor onlyMobile />
                <div className="year-content">
                  <FilterButton
                    label={yearTranslation.ctaLabel}
                    buttonLabel={showNumResults}
                    isDisabled={!!!totalVehicles}
                    filterSelectTracking={() =>
                      filterSelectTracking('multi_value', filterNameMap.year)
                    }
                  >
                    <FilterYear
                      translation={yearTranslation}
                      years={filters?.years}
                      onHandleYearSelection={onHandleFilterChange}
                      onHandleAllSelection={onHandleSelectAllFilter}
                      isMobile={isMobile}
                    />
                  </FilterButton>
                </div>
                <LineDivisor onlyMobile />
                <div className="price-content">
                  <FilterButton
                    label={priceTranslation.ctaLabel}
                    buttonLabel={showNumResults}
                    isDisabled={!!!totalVehicles}
                    filterSelectTracking={() =>
                      filterSelectTracking('range', filterNameMap.price)
                    }
                  >
                    <FilterPrice
                      onChangeUserPrice={setUserPrice}
                      onHandleRangeCommitted={onHandleFilterPriceApply}
                      currentPrice={userPrice}
                      translation={priceTranslation}
                      config={price}
                      isMobile={isMobile}
                    />
                  </FilterButton>
                </div>
                <LineDivisor onlyMobile />
                <div className="others-content">
                  <FilterButton
                    label="More Filters"
                    buttonLabel={showNumResults}
                    isDisabled={!!!totalVehicles}
                    filterSelectTracking={() =>
                      filterSelectTracking('complex', 'More Filters')
                    }
                  >
                    <StyledOtherFiltersContainer>
                      <div className="mileage">
                        <FilterMileage
                          translation={mileageTranslation}
                          currentMileage={userMileage}
                          config={mileage}
                          onChangeUserMileage={setUserMileage}
                          onHandleRangeCommitted={onHandleFilterMileageApply}
                          isMobile={isMobile}
                        />
                      </div>
                      <LineDivisor onlyMobile />
                      <div className="transmission">
                        <FilterTransmission
                          translation={transmissionTranslation}
                          onHandleAllSelection={onHandleSelectAllFilter}
                          onHandleTransmissionSelection={onHandleFilterChange}
                          transmissions={filters?.transmissionType}
                          isMobile={isMobile}
                        />
                      </div>
                      <LineDivisor onlyMobile />
                      <div className="fuel-type">
                        <FilterFuelType
                          translation={fuelTypeTranslation}
                          onHandleFuelTypeSelection={onHandleFilterChange}
                          fuelTypes={filters?.fuelType}
                          onHandleAllSelection={onHandleSelectAllFilter}
                          isMobile={isMobile}
                        />
                      </div>
                      <LineDivisor onlyMobile />
                      <div className="exterior-color">
                        <FilterExteriorColor
                          onHandleColorCheck={onHandleFilterChange}
                          translation={exteriorColorTranslation}
                          colors={filters?.exteriorColor}
                          isMobile={isMobile}
                        />
                      </div>
                      <LineDivisor onlyMobile />
                      <div className="interior-color">
                        <FilterInteriorColor
                          onHandleColorCheck={onHandleFilterChange}
                          translation={interiorColorTranslation}
                          colors={filters?.interiorColor}
                          isMobile={isMobile}
                        />
                      </div>
                    </StyledOtherFiltersContainer>
                  </FilterButton>
                </div>
              </div>
            </div>
            <div className="modal_footer-content">
              {/* @ts-ignore */}
              <ButtonCustom
                onClick={onHandleFiltersModalChange}
                appearance={ButtonAppearance.Primary}
                stretchContent
              >
                {showNumResults}
              </ButtonCustom>
              {/* @ts-ignore */}
              <ButtonCustom
                onClick={onHandleClearAllFilters}
                appearance={ButtonAppearance.Secondary}
                stretchContent
              >
                Clear Filters
              </ButtonCustom>
            </div>
          </div>
          {!isMobile && (
            <div className='online-buying' style={{flex: 1}}>
              <FilterOnlineBuying />
            </div>
          )}
        </StyledFiltersContentContainer>
        <StyledStickyFiltersContainer isSticky={isSticky}>
          <StyledFiltersBarContainer isMobile={isMobile}>
            {!!filters?.appliedFilters?.length && !isMobile && (
              <FilterAppliedBar
                onDeleteElement={onHandleFilterChange}
                onDeleteAllElements={onHandleClearAllFilters}
                filters={filters?.appliedFilters}
              />
            )}
          </StyledFiltersBarContainer>
          <StyledDropdownContainer isMobile={isMobile}>
            {!!totalPages && (
              <SearchResultsSortV2
                onHandleSelectedSort={onHandleSortChange}
                sortByValue={sortByValue}
              />
            )}
          </StyledDropdownContainer>
        </StyledStickyFiltersContainer>
      </StickyScrollContainer>
    </StyledFiltersSectionContainer>
  );
};

export default SearchFiltersSection;
